import { t } from '@/translations/i18n';
import { ETextAlignement } from '@/typescript/enums/ETextAlignement';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { ITemplate } from '@/typescript/interfaces/ITemplate';
import type { ITemplateConfiguration } from '@/typescript/interfaces/ITemplateConfiguration';
import type { ITextboxField } from '@/typescript/interfaces/ITextboxField';
import type { ITextField } from '@/typescript/interfaces/ITextField';

/**
 * Generates a UUID v4 compatible string
 * Fallback for development environments where crypto.randomUUID is not available
 */
function generateUUID(): string {
    // Check if crypto.randomUUID is available (HTTPS context)
    if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
    }

    // Fallback for development (HTTP context)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function getEmptyTemplate(noFields = false): ITemplate {
    return {
        name: t('custom_template_name'),
        thumbnailUrl: '/images/custom_template_thumbnail.jpg',
        configuration: noFields ? { fields: [] } : getEmptyTemplateConfiguration(),
    };
}

function getEmptyTemplateConfiguration(): ITemplateConfiguration {
    return {
        fields: [{ ...getEmptyTextboxField(), side: 'recto' }],
    };
}

function getEmptyTextField(): ITextField {
    return {
        id: generateUUID(),
        editable: true,
        transform: {
            position: {
                x: 0,
                y: 0,
            },
            angle: 0,
            scale: {
                x: 1,
                y: 1,
            },
        },
        textAlign: ETextAlignement.LEFT,
        characterSpacing: 0,
        lineSpacing: 0,
        font: 'AmbroiseStd-Regular',
        fontSize: 25,
        defaultText: 'Votre texte',
        color: '#000000',
        isBold: false,
        isUnderlined: false,
        isItalic: false,
    };
}

function getEmptyTextboxField(): ITextboxField {
    const textField = getEmptyTextField();
    return {
        ...textField,
        textAlign: ETextAlignement.CENTER,
        transform: {
            position: {
                x: 0,
                y: 0,
            },
            angle: 0,
            scale: {
                x: 1,
                y: 1,
            },
            size: {
                width: 500,
                height: 0,
            },
        },
    };
}

function getEmptyImageField(): IImageField {
    return {
        id: generateUUID(),
        transform: {
            position: {
                x: 0,
                y: 0,
            },
            angle: 0,
            scale: {
                x: 1,
                y: 1,
            },
        },
        editable: true,
    };
}

export { generateUUID, getEmptyImageField, getEmptyTemplate, getEmptyTemplateConfiguration, getEmptyTextboxField, getEmptyTextField };
